import { useState, useRef, useEffect } from 'react';
import { Bell, X, TrendingDown, TrendingUp, Phone, CheckCheck, Wifi, WifiOff, BellRing, BellOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const NotificationBell = ({ notifications, unreadCount, connected, onClear, onMarkAllRead, push }) => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleOpen = () => {
    setOpen(!open);
    if (!open && unreadCount > 0) onMarkAllRead();
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const getIcon = (notif) => {
    if (notif.type === 'alert_triggered') {
      return notif.trigger_type === 'min'
        ? <TrendingDown className="w-4 h-4 text-[#10D183]" />
        : <TrendingUp className="w-4 h-4 text-red-500" />;
    }
    if (notif.type === 'callback_request') return <Phone className="w-4 h-4 text-[#397DFF]" />;
    return <Bell className="w-4 h-4 text-[#687175]" />;
  };

  const getTitle = (notif) => {
    if (notif.type === 'alert_triggered') return notif.trigger_type === 'min' ? 'Prix bas atteint' : 'Prix haut atteint';
    if (notif.type === 'callback_request') return 'Demande de rappel';
    return 'Notification';
  };

  const getDesc = (notif) => {
    if (notif.type === 'alert_triggered') return `${notif.contract_name} a ${notif.current_price} EUR/MWh - ${notif.client_name}`;
    if (notif.type === 'callback_request') return `${notif.client_name} souhaite etre rappele`;
    return '';
  };

  const handlePushToggle = async () => {
    if (push.subscribed) {
      await push.unsubscribe();
    } else {
      await push.subscribe();
    }
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell-container">
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-lg hover:bg-[#F5F6F9] transition-colors"
        data-testid="notification-bell-btn"
      >
        <Bell className={`w-5 h-5 ${unreadCount > 0 ? 'text-[#397DFF]' : 'text-[#687175]'}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-[#BFC6CC]/30 shadow-xl z-50 overflow-hidden"
            data-testid="notification-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#BFC6CC]/20">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-[#191D1E]">Notifications</h3>
                {connected ? (
                  <Wifi className="w-3 h-3 text-[#10D183]" />
                ) : (
                  <WifiOff className="w-3 h-3 text-[#BFC6CC]" />
                )}
              </div>
              <div className="flex items-center gap-2">
                {push?.supported && (
                  <button
                    onClick={handlePushToggle}
                    className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors ${
                      push.subscribed
                        ? 'bg-[#10D183]/10 text-[#10D183]'
                        : 'bg-[#F5F6F9] text-[#687175] hover:text-[#397DFF]'
                    }`}
                    data-testid="push-toggle-btn"
                    title={push.subscribed ? 'Notifications push actives' : 'Activer les notifications push'}
                  >
                    {push.subscribed ? <BellRing className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}
                    <span className="hidden sm:inline">{push.subscribed ? 'Push ON' : 'Push'}</span>
                  </button>
                )}
                {notifications.length > 0 && (
                  <button
                    onClick={onMarkAllRead}
                    className="text-xs text-[#397DFF] hover:underline flex items-center gap-1"
                    data-testid="mark-all-read-btn"
                  >
                    <CheckCheck className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            {/* List */}
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="py-10 text-center">
                  <Bell className="w-8 h-8 text-[#BFC6CC] mx-auto mb-2" />
                  <p className="text-sm text-[#687175]">Aucune notification</p>
                  {push?.supported && !push.subscribed && (
                    <button
                      onClick={handlePushToggle}
                      className="mt-3 text-xs text-[#397DFF] hover:underline"
                      data-testid="enable-push-empty"
                    >
                      Activer les notifications push
                    </button>
                  )}
                </div>
              ) : (
                notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-[#BFC6CC]/10 hover:bg-[#F5F6F9]/50 transition-colors ${
                      !notif.read ? 'bg-[#397DFF]/[0.03]' : ''
                    }`}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-[#F5F6F9] flex items-center justify-center flex-shrink-0 mt-0.5">
                      {getIcon(notif)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-[#191D1E]">{getTitle(notif)}</p>
                        <span className="text-[10px] text-[#939BA0] flex-shrink-0 ml-2">{formatTime(notif.timestamp)}</span>
                      </div>
                      <p className="text-xs text-[#687175] mt-0.5 leading-relaxed">{getDesc(notif)}</p>
                    </div>
                    <button onClick={() => onClear(notif.id)} className="p-0.5 text-[#BFC6CC] hover:text-[#687175] flex-shrink-0">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
